import {
  Map as MapIcon,
  MessageCircle,
  Rocket,
  Swords,
  Users,
} from "lucide-react";

import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { ActionsTab } from "@/components/actions-tab";
import { AppHeader } from "@/components/app-header";
import { CampaignChat } from "@/components/campaign-chat";
import { CharacterTab } from "@/components/character-tab";
import { GameStateProvider } from "@/components/game-state-context";
import { MapsTab } from "@/components/maps-tab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCampaignSync } from "@/hooks/use-campaign-sync";
import { useParty } from "@/hooks/use-party";

const CAMPAIGN_ARCHIVED_ERROR = "campaign_archived";

function NavTabTrigger({
  value,

  children,
}: {
  value: string;

  children: React.ReactNode;
}) {
  return (
    <TabsTrigger
      className="flex h-full flex-1 flex-col items-center gap-1 rounded-none data-[state=active]:bg-transparent data-[state=active]:text-primary md:flex-initial md:flex-row md:gap-2 md:rounded-md md:px-4 md:py-2 md:data-[state=active]:bg-secondary"
      value={value}
    >
      {children}
    </TabsTrigger>
  );
}

// Pure navigation exit to the Launch hub — deliberately NOT a `TabsTrigger`
// (no `value`, so it never registers as tab state and no panel mounts for
// it) and it never fetches a campaign list; the hub owns that now.
function LaunchNavButton() {
  const navigate = useNavigate();
  return (
    <button
      className="flex h-full flex-1 flex-col items-center justify-center gap-1 rounded-none px-3 py-1.5 text-muted-foreground transition-colors hover:text-foreground md:flex-initial md:flex-row md:gap-2 md:rounded-md md:px-4 md:py-2"
      onClick={() => navigate("/launch")}
      type="button"
    >
      <Rocket className="h-5 w-5" />

      <span className="text-xs md:text-sm">Launch</span>
    </button>
  );
}

export function TtrpgApp({
  campaignId,
  token,
  children,
}: {
  campaignId: string;
  token?: string;
  children?: React.ReactNode;
}) {
  const [activeTab, setActiveTab] = useState("chat");
  const [pendingChoiceResolutionNonce, setPendingChoiceResolutionNonce] =
    useState(0);

  const { party, mutate: mutateParty } = useParty(campaignId);
  const pcs = party?.pcs ?? [];

  const [selectedCharId, setSelectedCharId] = useState<string | null>(null);

  // Set the default selection once the party data first arrives. Guards the
  // empty -> loaded transition without clobbering a user's later selection.
  useEffect(() => {
    if (selectedCharId === null && pcs.length > 0) {
      setSelectedCharId(pcs[0].id);
    }
  }, [selectedCharId, pcs]);

  const {
    campaign,
    mutate: mutateCampaign,
    isLoading,
    error,
  } = useCampaignSync(campaignId);

  const selectedPc = pcs.find((p) => p.id === selectedCharId) ?? pcs[0] ?? null;

  if (isLoading) {
    return <div>Loading campaign data...</div>;
  }

  if (error) {
    // The archived-campaign 409 surfaces here as a sentinel error message
    // (D2) — redirect into the Launch hub instead of rendering it raw.
    if (error.message === CAMPAIGN_ARCHIVED_ERROR) {
      return <Navigate replace to="/launch" />;
    }
    return <div>Error loading campaign: {error.message}</div>;
  }

  if (!campaign) {
    return <div>Campaign not found or not selected.</div>;
  }

  return (
    <div className="flex h-screen flex-col bg-background">
      <AppHeader />

      {children}

      <GameStateProvider campaignId={campaign.id}>
        <Tabs
          className="flex flex-1 flex-col overflow-hidden"
          onValueChange={setActiveTab}
          value={activeTab}
        >
          <TabsContent
            className="m-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
            forceMount
            value="chat"
          >
            <CampaignChat
              campaignId={campaign.id}
              pendingChoiceResolutionNonce={pendingChoiceResolutionNonce}
              token={token}
            />
          </TabsContent>

          <TabsContent
            className="m-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
            value="maps"
          >
            <MapsTab
              campaignId={campaign.id}
              onPendingChoiceResolved={() =>
                setPendingChoiceResolutionNonce((nonce) => nonce + 1)
              }
            />
          </TabsContent>

          <TabsContent
            className="m-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
            value="characters"
          >
            <CharacterTab
              campaignId={campaignId}
              onRefresh={() => mutateParty()}
              onSelect={setSelectedCharId}
              pcs={pcs}
              selectedId={selectedCharId}
              selectedPc={selectedPc}
            />
          </TabsContent>

          <TabsContent
            className="m-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
            value="actions"
          >
            <ActionsTab
              _sendMessage={undefined}
              campaign={campaign}
              character={selectedPc}
              mutateCampaign={mutateCampaign}
            />
          </TabsContent>

          {/* Bottom navigation for mobile, side tabs for desktop */}

          <TabsList className="flex h-16 justify-around rounded-none border-border border-t bg-card md:h-auto md:flex-row md:justify-start md:gap-2 md:border-t-0 md:border-b md:px-4 md:py-2">
            <NavTabTrigger value="chat">
              <MessageCircle className="h-5 w-5" />

              <span className="text-xs md:text-sm">Chat</span>
            </NavTabTrigger>

            <NavTabTrigger value="maps">
              <MapIcon className="h-5 w-5" />

              <span className="text-xs md:text-sm">Maps</span>
            </NavTabTrigger>

            <NavTabTrigger value="characters">
              <Users className="h-5 w-5" />

              <span className="text-xs md:text-sm">Character</span>
            </NavTabTrigger>

            <NavTabTrigger value="actions">
              <Swords className="h-5 w-5" />

              <span className="text-xs md:text-sm">Actions</span>
            </NavTabTrigger>

            <LaunchNavButton />
          </TabsList>
        </Tabs>
      </GameStateProvider>
    </div>
  );
}
